import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const BloodhoundForm = () => {
    const { apiFetch } = useAuth();
    const now = new Date();

    // Format today as YYYY-MM-DD for the date input default value
    const toDateInputValue = (date) => date.toISOString().split('T')[0];

    const [selectedDate, setSelectedDate] = useState(toDateInputValue(now));
    const [properties, setProperties] = useState([]);
    const [propertyID, setPropertyID] = useState(null);
    const [score, setScore] = useState('');
    const [loading, setLoading] = useState(false);

    // Derive year, month, day from the selected date string
    const [selYear, selMonth, selDay] = selectedDate
        ? selectedDate.split('-').map(Number)
        : [now.getFullYear(), now.getMonth() + 1, now.getDate()];

    // Max date: today (no future entries)
    const maxDate = toDateInputValue(now);

    // Load active properties on mount
    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await apiFetch('/api/Property/all');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    const active = data.data.filter(p => p.active !== false);
                    setProperties(active);
                    if (active.length > 0) setPropertyID(active[0].propertyID);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
            }
        };
        fetchProperties();
    }, [apiFetch]);

    // Fetch existing score whenever date or property changes
    useEffect(() => {
        if (!propertyID || !selectedDate) return;

        const fetchScore = async () => {
            try {
                const res = await apiFetch(
                    `/api/Ops/GetBloodhoundScore?year=${selYear}&month=${selMonth}&day=${selDay}&propertyId=${propertyID}`
                );
                const data = await res.json();
                setScore(data.found ? data.score.toString() : '');
            } catch (catchError) {
                Sentry.captureException(catchError);
                setScore('');
            }
        };

        fetchScore();
    }, [selectedDate, propertyID, apiFetch]);

    const handleScoreChange = (e) => {
        let value = e.target.value.replace(',', '.');
        if (value === '' || /^(\d+(\.\d{0,2})?)$/.test(value)) {
            setScore(value);
        }
    };

    const handleSave = async () => {
        if (!score) {
            toast.error('Score is required.');
            return;
        }
        if (!propertyID) {
            toast.error('Please select a property.');
            return;
        }
        setLoading(true);

        const payload = {
            year: selYear,
            month: selMonth,
            dayOfMonth: selDay,
            propertyID,
            score: parseFloat(score)
        };

        try {
            const response = await apiFetch('/api/Ops/SaveBloodhoundScore', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (response.ok && data.success) {
                toast.success('Score saved successfully!');
            } else {
                toast.error(data.message || 'Failed to save score.');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('An error occurred while saving.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <form className="bloodhound-form" onSubmit={e => e.preventDefault()}>
            <h2>Submit Bloodhound Score</h2>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="date">Date:</label>
                <input
                    id="date"
                    type="date"
                    value={selectedDate}
                    max={maxDate}
                    onChange={e => setSelectedDate(e.target.value)}
                />
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="property">Property:</label>
                <select
                    id="property"
                    value={propertyID ?? ''}
                    onChange={e => setPropertyID(Number(e.target.value))}
                >
                    {properties.length > 0 ? (
                        properties.map(p => (
                            <option key={p.propertyID} value={p.propertyID}>{p.propertyName}</option>
                        ))
                    ) : (
                        <option value="">Loading properties...</option>
                    )}
                </select>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label htmlFor="score">Score:</label>
                <input
                    id="score"
                    type="text"
                    inputMode="decimal"
                    pattern="^\d+([.,]\d{1,2})?$"
                    value={score}
                    onChange={handleScoreChange}
                    placeholder="e.g. 99.45"
                    autoComplete="off"
                />
            </div>
            <button type="button" style={{ width: '100%' }} onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
            </button>
        </form>
    );
};

export default BloodhoundForm;